// pipe: P3.4 (track D) — the PURE, prop-only driver Trip-Log form view (the
// Monday Driver Log §6 fields; the Vitest-tested seam). Mobile-first: stacked
// touch-friendly fields inside the shared <DriverScreen> frame, reusing the
// pickup-exec css module (same field/label primitives — DRY, no parallel kit).
//
// Import discipline (mirrors pickup-exec-view.tsx): ONLY @affine/component
// subpaths, @affine/i18n (mocked under happy-dom), the shared driver-screen
// frame + css modules, and TYPE-ONLY service imports. The container
// (trip-log.tsx) owns fetch/state/effects/navigation.
//
// THE LOAD-BEARING INVARIANT: mileage/duration render STRICTLY from the
// SERVER-derived figures on the saved trip (never re-derived client-side —
// the server formulas are the single §6 authority), and NO cost figure exists
// on this surface at all (costs are internal, Erp-gated report data).
import { Button } from '@affine/component/ui/button';
import { Input } from '@affine/component/ui/input';
import { Loading } from '@affine/component/ui/loading';
import { useI18n } from '@affine/i18n';

import type { TripLogRecord } from '../services/driver-erp';
import { DriverScreen } from './driver-screen';
import * as ui from './driver-ui.css';
import * as styles from './pickup-exec.css';

/** The string-bound form fields (parsed/assembled by the container). */
export interface TripFormState {
  crew_size: string;
  depart_facility_ts: string;
  arrive_customer_ts: string;
  depart_customer_ts: string;
  arrive_facility_ts: string;
  odometer_start: string;
  odometer_dock: string;
  odometer_return: string;
  seal_no: string;
  pallet_count: string;
  notes: string;
}

export interface TripLogViewProps {
  loading?: boolean;
  error?: string | null;
  form: TripFormState;
  onField: (key: keyof TripFormState, value: string) => void;
  /** The last SAVED trip (carries the server-derived mileage/duration). */
  saved: TripLogRecord | null;
  onSave: () => void;
  saving?: boolean;
  saveError?: string | null;
  savedNotice?: boolean;
  onBack?: () => void;
}

export function TripLogView(props: TripLogViewProps) {
  const t = useI18n();

  if (props.error) {
    return (
      <DriverScreen onBack={props.onBack}>
        <div className={ui.errorPanel} role="alert">
          {t['com.affine.pipe.driver.trip.error']()}
        </div>
      </DriverScreen>
    );
  }
  if (props.loading) {
    return (
      <DriverScreen onBack={props.onBack}>
        <div className={ui.statusPanel} role="status">
          <Loading size={24} />
          {t['com.affine.pipe.driver.trip.loading']()}
        </div>
      </DriverScreen>
    );
  }

  const timeField = (
    key: keyof TripFormState,
    label:
      | 'com.affine.pipe.driver.trip.depart-facility'
      | 'com.affine.pipe.driver.trip.arrive-customer'
      | 'com.affine.pipe.driver.trip.depart-customer'
      | 'com.affine.pipe.driver.trip.arrive-facility'
  ) => (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{t[label]()}</span>
      <Input
        type="datetime-local"
        value={props.form[key]}
        onChange={v => props.onField(key, v)}
      />
    </label>
  );

  const numberField = (
    key: keyof TripFormState,
    label:
      | 'com.affine.pipe.driver.trip.crew'
      | 'com.affine.pipe.driver.trip.odometer-start'
      | 'com.affine.pipe.driver.trip.odometer-dock'
      | 'com.affine.pipe.driver.trip.odometer-return'
      | 'com.affine.pipe.driver.trip.pallets'
  ) => (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{t[label]()}</span>
      <Input
        type="number"
        inputMode="decimal"
        value={props.form[key]}
        onChange={v => props.onField(key, v)}
      />
    </label>
  );

  return (
    <DriverScreen
      title={t['com.affine.pipe.driver.trip.title']()}
      onBack={props.onBack}
    >
      <section className={styles.section}>
        {numberField('crew_size', 'com.affine.pipe.driver.trip.crew')}
        {timeField(
          'depart_facility_ts',
          'com.affine.pipe.driver.trip.depart-facility'
        )}
        {timeField(
          'arrive_customer_ts',
          'com.affine.pipe.driver.trip.arrive-customer'
        )}
        {timeField(
          'depart_customer_ts',
          'com.affine.pipe.driver.trip.depart-customer'
        )}
        {timeField(
          'arrive_facility_ts',
          'com.affine.pipe.driver.trip.arrive-facility'
        )}
        {numberField(
          'odometer_start',
          'com.affine.pipe.driver.trip.odometer-start'
        )}
        {numberField(
          'odometer_dock',
          'com.affine.pipe.driver.trip.odometer-dock'
        )}
        {numberField(
          'odometer_return',
          'com.affine.pipe.driver.trip.odometer-return'
        )}
        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            {t['com.affine.pipe.driver.trip.seal']()}
          </span>
          <Input
            value={props.form.seal_no}
            onChange={v => props.onField('seal_no', v)}
          />
        </label>
        {numberField('pallet_count', 'com.affine.pipe.driver.trip.pallets')}
        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            {t['com.affine.pipe.driver.trip.notes']()}
          </span>
          <Input
            value={props.form.notes}
            onChange={v => props.onField('notes', v)}
          />
        </label>
      </section>

      {/* SERVER-derived figures (present after a save; never client-derived). */}
      {props.saved ? (
        <section className={styles.section}>
          <div className={styles.row}>
            <span className={styles.fieldLabel}>
              {t['com.affine.pipe.driver.trip.mileage']()}
            </span>
            <span className={styles.declaredValue}>
              {props.saved.mileage ?? '—'}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.fieldLabel}>
              {t['com.affine.pipe.driver.trip.duration']()}
            </span>
            <span className={styles.declaredValue}>
              {props.saved.duration_hr ?? '—'}
            </span>
          </div>
        </section>
      ) : null}

      {props.savedNotice ? (
        <div className={ui.statusPanel} role="status">
          {t['com.affine.pipe.driver.trip.saved']()}
        </div>
      ) : null}
      {props.saveError ? (
        <div className={ui.errorPanel} role="alert">
          {t['com.affine.pipe.driver.trip.save-error']()}
        </div>
      ) : null}

      <div className={styles.actionBar}>
        <Button
          variant="primary"
          size="large"
          block
          disabled={!!props.saving}
          onClick={props.onSave}
        >
          {props.saving
            ? t['com.affine.pipe.driver.trip.saving']()
            : t['com.affine.pipe.driver.trip.save']()}
        </Button>
      </div>
    </DriverScreen>
  );
}
